import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PostCard from '../components/feed/PostCard';
import { 
  TrendingUp, Users, Hash, Sparkles, Loader2, UserPlus, 
  Heart, MessageCircle, ArrowLeft, Search, Grid3X3, Play,
  ChevronRight, Flame, Crown, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { getInitials } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DiscoveryPage() {
  const { token, user, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'trending');
  const [trendingHashtags, setTrendingHashtags] = useState([]);
  const [popularCreators, setPopularCreators] = useState([]);
  const [viralPosts, setViralPosts] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingIds, setFollowingIds] = useState(new Set());

  // Fetch all discovery data
  const fetchDiscoveryData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [hashtagsRes, creatorsRes, postsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/discover/trending-hashtags?limit=15`, { headers }),
        axios.get(`${API}/discover/popular-creators?limit=12`, { headers }),
        axios.get(`${API}/discover/viral-posts?limit=20`, { headers }),
        axios.get(`${API}/discover/categories`, { headers })
      ]);
      
      setTrendingHashtags(hashtagsRes.data);
      setPopularCreators(creatorsRes.data);
      setViralPosts(postsRes.data);
      setCategories(categoriesRes.data);
      
      // Get suggested users if logged in
      if (token) {
        try {
          const suggestedRes = await axios.get(`${API}/discover/suggested-users?limit=8`, { headers });
          setSuggestedUsers(suggestedRes.data);
        } catch (e) {}
      }
    } catch (error) {
      console.error('Failed to fetch discovery data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDiscoveryData();
  }, [fetchDiscoveryData]);

  const handleFollow = async (userId) => {
    if (!token) {
      toast.error('Please login to follow users');
      return;
    }

    try {
      if (followingIds.has(userId)) {
        await axios.post(`${API}/users/${userId}/unfollow`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFollowingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
        toast.success('Unfollowed');
      } else {
        await axios.post(`${API}/users/${userId}/follow`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFollowingIds(prev => new Set([...prev, userId]));
        toast.success('Following!');
      }
    } catch (error) {
      toast.error('Failed to update follow status');
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-20" data-testid="discovery-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-primary" />
              <h1 className="font-bold text-lg">Discover</h1>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/search')}>
            <Search className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="sticky top-14 z-20 bg-background/80 backdrop-blur-xl border-b border-border/30">
          <TabsList className="w-full justify-start overflow-x-auto scrollbar-hide bg-transparent h-12 px-4 gap-2">
            <TabsTrigger value="trending" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Flame className="w-4 h-4 mr-1" />
              Trending
            </TabsTrigger>
            <TabsTrigger value="creators" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Crown className="w-4 h-4 mr-1" />
              Creators
            </TabsTrigger>
            <TabsTrigger value="viral" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <TrendingUp className="w-4 h-4 mr-1" />
              Viral
            </TabsTrigger>
            <TabsTrigger value="categories" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Grid3X3 className="w-4 h-4 mr-1" />
              Categories
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Trending Hashtags Tab */}
        <TabsContent value="trending" className="m-0 p-4 space-y-6">
          {/* Suggested Users (if logged in) */}
          {suggestedUsers.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Suggested For You
                </h2>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                {suggestedUsers.map(creator => (
                  <Card key={creator.id} className="shrink-0 w-36 overflow-hidden">
                    <CardContent className="p-3 text-center">
                      <Avatar 
                        className="w-16 h-16 mx-auto mb-2 cursor-pointer"
                        onClick={() => navigate(`/profile/${creator.username}`)}
                      >
                        <AvatarImage src={creator.avatar} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {getInitials(creator.display_name || creator.username)}
                        </AvatarFallback>
                      </Avatar>
                      <p className="font-semibold text-sm truncate">{creator.display_name || creator.username}</p>
                      <p className="text-xs text-muted-foreground truncate">@{creator.username}</p>
                      {creator.mutual_followers > 0 && (
                        <p className="text-xs text-primary mt-1">{creator.mutual_followers} mutual</p>
                      )}
                      <Button
                        size="sm"
                        variant={followingIds.has(creator.id) ? "outline" : "default"}
                        className="w-full mt-2 rounded-full text-xs"
                        onClick={() => handleFollow(creator.id)}
                        data-testid={`follow-suggested-${creator.id}`}
                      >
                        {followingIds.has(creator.id) ? 'Following' : 'Follow'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {/* Trending Hashtags */}
          <section>
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <Hash className="w-5 h-5 text-primary" />
              Trending Hashtags
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {trendingHashtags.map((tag, index) => (
                <Card 
                  key={tag.hashtag}
                  className="cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => navigate(`/explore/hashtag/${tag.hashtag}`)}
                  data-testid={`hashtag-${tag.hashtag}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-pink-500/20 flex items-center justify-center shrink-0">
                      {tag.preview_media ? (
                        tag.preview_media_type === 'video' ? (
                          <Play className="w-5 h-5 text-primary" />
                        ) : (
                          <img src={tag.preview_media} alt="" className="w-full h-full object-cover rounded-xl" />
                        )
                      ) : (
                        <span className="text-lg font-bold text-primary">#{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold">#{tag.hashtag}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatNumber(tag.post_count)} posts
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="flex items-center gap-1 text-pink-500">
                        <Heart className="w-4 h-4" />
                        <span className="text-sm">{formatNumber(tag.total_likes)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </TabsContent>

        {/* Popular Creators Tab */}
        <TabsContent value="creators" className="m-0 p-4">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-primary" />
            Popular Creators
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {popularCreators.map((creator, index) => (
              <Card 
                key={creator.id}
                className="overflow-hidden"
                data-testid={`creator-${creator.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {index < 3 && (
                        <div className={`absolute -top-1 -left-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                          index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-amber-600'
                        }`}>
                          {index + 1}
                        </div>
                      )}
                      <Avatar 
                        className="w-14 h-14 cursor-pointer"
                        onClick={() => navigate(`/profile/${creator.username}`)}
                      >
                        <AvatarImage src={creator.avatar} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {getInitials(creator.display_name || creator.username)}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <p className="font-semibold truncate">{creator.display_name || creator.username}</p>
                        {creator.is_verified && (
                          <svg className="w-4 h-4 text-primary shrink-0" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                          </svg>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">@{creator.username}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {formatNumber(creator.followers_count)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Grid3X3 className="w-3 h-3" />
                          {creator.posts_count}
                        </span>
                      </div>
                    </div>
                    {creator.id !== user?.id && (
                      <Button
                        size="sm"
                        variant={followingIds.has(creator.id) ? "outline" : "default"}
                        className="rounded-full shrink-0"
                        onClick={() => handleFollow(creator.id)}
                      >
                        {followingIds.has(creator.id) ? 'Following' : 'Follow'}
                      </Button>
                    )}
                  </div>
                  {creator.bio && (
                    <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{creator.bio}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Viral Posts Tab */}
        <TabsContent value="viral" className="m-0">
          <div className="p-4">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Viral Posts
            </h2>
          </div>
          <div className="divide-y divide-border/40">
            {viralPosts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Flame className="w-16 h-16 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No viral posts yet</p>
                <p className="text-sm text-muted-foreground">Check back later for trending content</p>
              </div>
            ) : (
              viralPosts.map((post, index) => (
                <div key={post.id} className="relative">
                  {index < 3 && (
                    <div className="absolute top-4 left-4 z-10 px-2 py-1 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                      <Flame className="w-3 h-3" />
                      #{index + 1} Viral
                    </div>
                  )}
                  <PostCard post={post} onUpdate={fetchDiscoveryData} />
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="m-0 p-4">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Grid3X3 className="w-5 h-5 text-primary" />
            Browse by Category
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {categories.map((category) => (
              <Card
                key={category.id}
                className="cursor-pointer hover:bg-secondary/50 transition-all hover:scale-105"
                onClick={() => navigate(`/explore/hashtag/${category.hashtags[0]}`)}
                data-testid={`category-${category.id}`}
              >
                <CardContent className="p-4 text-center">
                  <div className="text-3xl mb-2">{category.icon}</div>
                  <p className="font-semibold">{category.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatNumber(category.post_count)} posts
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
